/**
 * 复习页 — SRS 卡片翻面流程
 *
 * 对齐 features.md 7.2-7.6：复习卡片 + 四档评分
 * 对齐 pages.md §七：复习页面
 */

import { useEffect } from 'react';
import { useReviewStore } from '../stores/review';
import { useKnowledgeStore } from '../stores/knowledge';
import { GRADE_OPTIONS, formatDue, type SrsGrade } from '../lib/srs';
import { Button } from '@hump/ui';

export function ReviewPage() {
  const { current, flipped, reviewedCount, totalCount, startReview, flip, grade, cards } = useReviewStore();
  const { items } = useKnowledgeStore();

  useEffect(() => {
    startReview();
  }, [startReview]);

  // 无待复习
  if (!current && totalCount === 0) {
    const dueCount = items.filter((item) => {
      const card = cards[item.id];
      return !card || card.due.getTime() <= Date.now();
    }).length;

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-6xl">🐪</p>
        <p className="text-lg text-charcoal/60 dark:text-charcoal-light/60">
          {dueCount > 0 ? `有 ${dueCount} 个待复习` : '今日无待复习'}
        </p>
        {dueCount > 0 && (
          <Button variant="primary" onClick={() => startReview()}>
            开始复习
          </Button>
        )}
      </div>
    );
  }

  // 复习完成
  if (!current && totalCount > 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-6xl">🎉</p>
        <p className="text-xl text-charcoal dark:text-charcoal-light">
          走完了这一程
        </p>
        <p className="text-sm text-charcoal/50 dark:text-charcoal-light/50">
          完成了 {reviewedCount} 张卡片的复习。驼峰又鼓了一点。
        </p>
        <Button variant="ghost" onClick={() => startReview()}>
          再复习一轮
        </Button>
      </div>
    );
  }

  // 复习中
  return (
    <div className="max-w-lg mx-auto p-6">
      {/* 进度 */}
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-charcoal/50 dark:text-charcoal-light/50">
          {reviewedCount + 1} / {totalCount}
        </span>
        <span className="text-xs text-charcoal/30 dark:text-charcoal-light/30">
          下次：{formatDue(cards[current!.id])}
        </span>
      </div>

      {/* 卡片 */}
      <div
        className="min-h-[300px] flex items-center justify-center p-8 rounded-xl bg-white dark:bg-warm-dark shadow-md cursor-pointer"
        onClick={() => !flipped && flip()}
      >
        {!flipped ? (
          <div className="text-center">
            <p className="text-4xl font-serif text-charcoal dark:text-charcoal-light">
              {current!.word}
            </p>
            {current!.phonetic && (
              <p className="text-base text-charcoal/50 dark:text-charcoal-light/50 mt-2">
                {current!.phonetic}
              </p>
            )}
            <p className="text-xs text-charcoal/30 dark:text-charcoal-light/30 mt-4">
              点击翻面查看释义
            </p>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-lg text-charcoal/80 dark:text-charcoal-light/80 mb-3">
              {current!.word}
            </p>
            {current!.definitions?.map((d, i) => (
              <p key={i} className="text-base text-charcoal dark:text-charcoal-light mb-1">
                {d}
              </p>
            ))}
            {current!.context && (
              <p className="text-sm text-charcoal/40 dark:text-charcoal-light/40 italic mt-3">
                {current!.context}
              </p>
            )}
          </div>
        )}
      </div>

      {/* 评分按钮 */}
      {flipped && (
        <div className="grid grid-cols-4 gap-2 mt-6">
          {GRADE_OPTIONS.map((opt) => (
            <button
              key={opt.grade}
              onClick={() => grade(opt.grade as SrsGrade)}
              className={`py-2.5 rounded-md text-sm font-medium transition-colors ${opt.color} hover:opacity-90`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
